import { supabase } from "../supabase/client";
import { demoService } from "../demo/demoService";
import { demoAPI } from "./demoApi";
import { isAbortError } from "../utils/abort";

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : "/api";

export interface TransactionAnalysisResult {
  success: boolean;
  linkedCount: number;
  linkedPairs: Array<{
    transaction: { id: string; name: string; amount: number };
    expense: { id: string; name: string; amount: number };
  }>;
  message: string;
}

export interface Transaction {
  id: string;
  name: string;
  amount: number;
  date: string;
  merchant_name?: string;
  internal_category?: string[];
  account_id?: string;
}

export interface LinkTransactionResult {
  success: boolean;
  expense: any;
  message: string;
}

class TransactionService {
  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    if (demoService.isDemo()) {
      throw new Error("Demo mode: backend requests disabled");
    }
    // Get Supabase session token
    let session = null;
    try {
      const {
        data: { session: activeSession },
      } = await supabase.auth.getSession();
      session = activeSession;
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] transactionService.getSession aborted", error);
      } else {
        console.error("[DEBUG] transactionService.getSession failed", error);
      }
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    // Add Authorization header if session exists
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    const defaultOptions = {
      ...options,
      headers,
    };

    const response = await fetch(url, defaultOptions);

    // Handle auth errors
    if (response.status === 401 || response.status === 403) {
      console.error("Authentication error:", response.status);
      if (window.location.pathname !== "/login")
        console.warn(
          "Auth required; suppressing hard redirect during request."
        );
      throw new Error("Authentication required");
    }

    if (!response.ok) {
      throw new Error(
        `API call failed: ${response.status} ${response.statusText}`
      );
    }
    return response.json();
  }

  async analyzeTransactions(
    days: number = 60
  ): Promise<TransactionAnalysisResult> {
    if (demoService.isDemo()) {
      return {
        success: true,
        linkedCount: 0,
        linkedPairs: [],
        message: "Demo mode: analysis skipped",
      };
    }
    try {
      const result = await this.fetchWithAuth(
        `${API_BASE_URL}/analyze-transactions`,
        {
          method: "POST",
          body: JSON.stringify({ days }),
        }
      );
      return result;
    } catch (error) {
      console.error("Failed to analyze transactions:", error);
      throw error;
    }
  }

  async getRecentTransactions(days: number = 60): Promise<Transaction[]> {
    try {
      if (demoService.isDemo()) {
        return (demoAPI.getTransactions() as any[]).map((tx) => ({
          id: tx.transaction_id || tx.id,
          name: tx.name || tx.merchant_name || "Unknown Transaction",
          amount: Math.abs(Number(tx.amount) || 0),
          date: tx.date,
          merchant_name: tx.merchant_name,
          internal_category: tx.internal_category,
          account_id: tx.account_id,
        }));
      }
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const startDateStr = startDate.toISOString().split("T")[0];
      const endDateStr = endDate.toISOString().split("T")[0];

      console.log(
        `Fetching transactions from ${startDateStr} to ${endDateStr}`
      );

      // Get CSV transactions (these are working)
      const csvResult = await this.fetchWithAuth(
        `${API_BASE_URL}/csv-transactions`
      );
      console.log("CSV transactions:", csvResult.transactions?.length || 0);

      // Try to get Plaid transactions (but don't fail if they're not available)
      let plaidTransactions = [];
      try {
        const plaidUrl = `${API_BASE_URL}/plaid/transactions?start_date=${encodeURIComponent(
          startDateStr
        )}&end_date=${encodeURIComponent(endDateStr)}`;
        const plaidResult = await this.fetchWithAuth(plaidUrl);
        plaidTransactions = plaidResult.transactions || [];
        console.log("Plaid transactions:", plaidTransactions.length);
      } catch (plaidError) {
        console.warn("Plaid transactions not available:", plaidError);
      }

      // Combine and format transactions
      const allTransactions = [
        ...plaidTransactions,
        ...(csvResult.transactions || []),
      ];
      console.log("Total raw transactions:", allTransactions.length);

      // Filter by date range and format
      const filteredTransactions = allTransactions
        .filter((tx) => {
          const txDate = new Date(tx.date);
          return txDate >= startDate && txDate <= endDate;
        })
        .map((tx) => ({
          id: tx.transaction_id || tx.id,
          name: tx.name || tx.merchant_name || "Unknown Transaction",
          amount: Math.abs(Number(tx.amount) || 0),
          date: tx.date,
          merchant_name: tx.merchant_name,
          internal_category: tx.internal_category,
          account_id: tx.account_id,
        }))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

      console.log("Filtered transactions:", filteredTransactions.length);
      return filteredTransactions;
    } catch (error) {
      console.error("Failed to fetch recent transactions:", error);
      return [];
    }
  }

  async linkTransactionToExpense(
    transactionId: string,
    expenseId: string
  ): Promise<LinkTransactionResult> {
    if (demoService.isDemo()) {
      return {
        success: true,
        expense: null,
        message: "Demo mode: link skipped",
      };
    }
    try {
      const result = await this.fetchWithAuth(
        `${API_BASE_URL}/transactions/${transactionId}/link-expense`,
        {
          method: "POST",
          body: JSON.stringify({ expenseId }),
        }
      );
      return result;
    } catch (error) {
      console.error("Failed to link transaction to expense:", error);
      throw error;
    }
  }
}

export const transactionService = new TransactionService();
